// tslint:disable: no-empty
import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { callApi, TApiResponse } from '../api'
import { useAuthContext } from '../auth'
import { useAccountContext } from '../account'
import { IScheduler, TSchedulerActions } from '../types'
import { profileHeaderToken } from '../utils'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialSchedulerState: IScheduler = {
	loading: false,
	availabilityViewerElementToken: null,
	manageAvailabilityElementToken: null,
	profiles: null,
}

const SchedulerContext: Context<{
	scheduler: IScheduler
	actions: TSchedulerActions
}> = createContext({
	scheduler: initialSchedulerState,
	actions: {
		attach: (code: string) => {},
		getAvailabilityViewerElementToken: (sub: string) => {},
		getManageAvailabilityElementToken: (token?: string) => {},
		setError: (error: string) => {},
		clearAvailabilityViewerElementToken: () => {},
		fetchCronofyProfiles: () => {},
		revokeCronofyProfiles: (profiles_ids: string[]): Promise<any> => null,
	},
})

export const SchedulerProvider = (props: PropsWithChildren<{}>) => {
	const { auth, actions: authActions } = useAuthContext()
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [availabilityViewerElementToken, setAvailabilityViewerElementToken] = useState<string | null>(null)
	const [profiles, setProfiles] = useState<any | null>(null)
	const [manageAvailabilityElementToken, setManageAvailabilityElementToken] = useState<string | null>(null)
	const { account } = useAccountContext()

	useEffect(() => {
		clearManageAvailabilityElementToken()
	}, [auth.user?.id])

	const fetchAttach = async (code: string) => {
		setLoading(true)

		const schedulerResponse: TApiResponse = await callApi('/scheduler/attach', {
			method: 'post',
			body: { code },
		})

		const { data } = schedulerResponse.payload

		if (schedulerResponse.ok) {
			setLoading(false)
			setProviderError(null)
			authActions.setUserFields({
				expertInfo: {
					...auth?.user?.expertInfo,
					cronofy_account_id: data.cronofy_account_id,
				},
			})
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot attach scheduler!')
			if (schedulerResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchCronofyProfiles = async () => {
		setLoading(true)

		const profilesResponse: TApiResponse = await callApi('/scheduler/profiles', {
			method: 'get',
		})

		const { data } = profilesResponse.payload

		if (profilesResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setProfiles(data.profiles)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get scheduler!')
			if (profilesResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const revokeCronofyProfiles = async (profiles_ids: string[]): Promise<any> => {
		setLoading(true)

		const profilesResponse: TApiResponse = await callApi('/scheduler/profiles/revoke', {
			method: 'post',
			body: { profiles_ids },
		})

		const { data } = profilesResponse.payload

		if (profilesResponse.ok) {
			const filteredProfiles = profiles.filter((profile: any) => !profiles_ids.includes(profile.profile_id))

			setLoading(false)
			setProviderError(null)
			setProfiles(filteredProfiles)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot revoke profiles!')
			if (profilesResponse.status === 401) {
				authActions.logout()
			}
		}

		return data
	}

	const fetchAvailabilityViewerElementToken = async (sub: string) => {
		setLoading(true)
		let paramObj = {}
		if (account.token) paramObj = { headers: profileHeaderToken(account.token) }
		const schedulerResponse: TApiResponse = await callApi('/scheduler/element-token', {
			method: 'post',
			body: { sub },
			paramObj,
		})

		const { data } = schedulerResponse.payload

		if (schedulerResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setAvailabilityViewerElementToken(data.elementToken)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get scheduler!')
			if (schedulerResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchManageAvailabilityElementToken = async (token?: string) => {
		setLoading(true)
		let paramObj = {}
		if (token) paramObj = profileHeaderToken(token)
		const schedulerResponse: TApiResponse = await callApi('/scheduler/element-token', {
			method: 'post',
			headers: paramObj,
		})

		const { data } = schedulerResponse.payload

		if (schedulerResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setManageAvailabilityElementToken(data.elementToken)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get scheduler!')
			if (schedulerResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const attach = useCallback(fetchAttach, [auth?.user?.expertInfo, authActions])
	const getAvailabilityViewerElementToken = useCallback(fetchAvailabilityViewerElementToken, [authActions])
	const getManageAvailabilityElementToken = useCallback(fetchManageAvailabilityElementToken, [authActions])
	const clearAvailabilityViewerElementToken = useCallback(() => {
		setAvailabilityViewerElementToken(null)
	}, [])
	const clearManageAvailabilityElementToken = useCallback(() => {
		setManageAvailabilityElementToken(null)
	}, [])

	const actions: TSchedulerActions = {
		attach,
		getAvailabilityViewerElementToken,
		getManageAvailabilityElementToken,
		setError: setProviderError,
		clearAvailabilityViewerElementToken,
		fetchCronofyProfiles,
		revokeCronofyProfiles,
	}

	return (
		<SchedulerContext.Provider
			value={{
				scheduler: {
					loading,
					error,
					availabilityViewerElementToken,
					manageAvailabilityElementToken,
					profiles,
				},
				actions,
			}}
		>
			{props.children}
		</SchedulerContext.Provider>
	)
}

export const useSchedulerContext: () => {
	scheduler: IScheduler
	actions: TSchedulerActions
} = () => {
	return useContext(SchedulerContext)
}
